import React, { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext(null);
const KEY = "cka_cart_v1";
const SHIP_KEY = "cka_ship_v1";

const devWarn = (...args) => {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn(...args);
  }
};

const loadInitial = () => {
  try {
    const raw = typeof window !== "undefined" ? localStorage.getItem(KEY) : null;
    if (raw) return JSON.parse(raw);
  } catch (err) {
    devWarn("Cart load failed:", err);
  }
  return [];
};

const loadShipping = () => {
  try {
    const raw = typeof window !== "undefined" ? localStorage.getItem(SHIP_KEY) : null;
    if (raw) return JSON.parse(raw);
  } catch (err) {
    devWarn("Shipping load failed:", err);
  }
  return null;
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadInitial);
  const [shippingOption, setShippingOption] = useState(loadShipping);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch (err) {
      devWarn("Cart save failed:", err);
    }
  }, [items]);

  useEffect(() => {
    try {
      if (shippingOption) {
        localStorage.setItem(SHIP_KEY, JSON.stringify(shippingOption));
      } else {
        localStorage.removeItem(SHIP_KEY);
      }
    } catch (err) {
      devWarn("Shipping save failed:", err);
    }
  }, [shippingOption]);

  const addItem = (product, qty = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.id === product.id);
      if (idx > -1) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + qty };
        return copy;
      }
      return [
        ...prev,
        {
          id: product.id,
          title: product.title,
          price: product.price,
          image_url: product.image_url,
          external_url: product.external_url,
          category: product.category || "other",
          qty,
        },
      ];
    });
    // Invalidate any prior shipping quote when cart changes
    setShippingOption(null);
  };

  const updateQty = (id, qty) => {
    setItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, qty: Math.max(1, qty) } : i))
    );
    setShippingOption(null);
  };

  const removeItem = (id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
    setShippingOption(null);
  };
  const clear = () => {
    setItems([]);
    setShippingOption(null);
  };

  const totalItems = items.reduce((a, b) => a + b.qty, 0);
  const subtotal = items.reduce((a, b) => a + b.qty * b.price, 0);
  const shippingCost = shippingOption ? Number(shippingOption.price) || 0 : 0;
  const totalPrice = subtotal + shippingCost;

  // Any physical items (non-digital) present?
  const hasPhysicalItems = items.some(
    (i) => (i.category || "other").toLowerCase() !== "digital"
  );

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        updateQty,
        removeItem,
        clear,
        totalItems,
        subtotal,
        shippingCost,
        totalPrice,
        shippingOption,
        setShippingOption,
        hasPhysicalItems,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
